import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { ROUTES } from '@motorway/mw-dealer-shared-components/utils';

import { ACCOUNT_CATEGORIES, ACCOUNT_SLUGS } from 'Components/pages/AccountPage/AccountPage.consts';

// TODO: Import the PRIMARY_NAVIGATION_ITEM_IDS from the shared components.
// This is a list of the strings that are used to identify the primary navigation items, e.g. 'marketplace'.
// This const is copied from DSC for now.
// import { PRIMARY_NAVIGATION_ITEM_IDS } from '@motorway/mw-dealer-shared-components/config/primaryNavigation/generatePrimaryNavigationConfig/generatePrimaryNavigationConfig';
export const PRIMARY_NAVIGATION_ITEM_IDS = {
	accountSettings: 'account_settings',
	avatar: 'avatar',
	bidsAndOffers: 'bids_offers',
	bidsAndOffersActive: 'bids_offers_active',
	bidsAndOffersDidntWin: 'bids_offers_didnt_win',
	bidsAndOffersNotAccepted: 'bids_offers_not_accepted',
	bidsAndOffersUnderOffer: 'bids_offers_under_offer',
	billing: 'billing',
	helpCenter: 'help_center',
	howItWorks: 'how_it_works',
	insights: 'insights',
	liveSale: 'live_sale',
	logo: 'logo',
	motorwayMove: 'motorway_move',
	motorwayPay: 'motorway_pay',
	pricing: 'pricing',
	products: 'products',
	purchases: 'purchases',
	purchasesCancelled: 'purchases_cancelled',
	purchasesComplete: 'purchases_complete',
	signIn: 'sign_in',
	signOut: 'sign_out',
	signUp: 'sign_up',
	userMenuHeader: 'user_menu_header',
	wallet: 'wallet',
} as const;

const { ACCOUNT, ACCOUNT_SETTINGS, HOW_IT_WORKS, INSIGHTS_LANDING, INSIGHTS_RESULTS, PRICING, VEHICLE_LIST } = ROUTES;
const { MOTORWAY_MOVE, MOTORWAY_PAY } = ROUTES;

const itemIdMap: { [key: string]: string } = {
	[VEHICLE_LIST.href]: PRIMARY_NAVIGATION_ITEM_IDS.liveSale,
	[ACCOUNT.href]: PRIMARY_NAVIGATION_ITEM_IDS.bidsAndOffers,
	[ACCOUNT_SETTINGS.href]: PRIMARY_NAVIGATION_ITEM_IDS.userMenuHeader,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.BIDS, ACCOUNT_SLUGS.ACTIVE)]: PRIMARY_NAVIGATION_ITEM_IDS.bidsAndOffersActive,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.BIDS, ACCOUNT_SLUGS.UNDER_OFFER)]: PRIMARY_NAVIGATION_ITEM_IDS.bidsAndOffersUnderOffer,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.BIDS, ACCOUNT_SLUGS.DIDNT_WIN)]: PRIMARY_NAVIGATION_ITEM_IDS.bidsAndOffersDidntWin,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.BIDS, ACCOUNT_SLUGS.NOT_ACCEPTED)]:
		PRIMARY_NAVIGATION_ITEM_IDS.bidsAndOffersNotAccepted,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.PURCHASES, ACCOUNT_SLUGS.COMPLETE)]: PRIMARY_NAVIGATION_ITEM_IDS.purchasesComplete,
	[ACCOUNT.as(ACCOUNT_CATEGORIES.PURCHASES, ACCOUNT_SLUGS.CANCELLED)]: PRIMARY_NAVIGATION_ITEM_IDS.purchasesCancelled,
	[INSIGHTS_LANDING.href]: PRIMARY_NAVIGATION_ITEM_IDS.insights,
	[INSIGHTS_RESULTS.href]: PRIMARY_NAVIGATION_ITEM_IDS.insights,
	[HOW_IT_WORKS.href]: PRIMARY_NAVIGATION_ITEM_IDS.howItWorks,
	[MOTORWAY_MOVE.href]: PRIMARY_NAVIGATION_ITEM_IDS.motorwayMove,
	[MOTORWAY_PAY.href]: PRIMARY_NAVIGATION_ITEM_IDS.motorwayPay,
	[PRICING.href]: PRIMARY_NAVIGATION_ITEM_IDS.pricing,
} as const;

type GetActiveItemIdParams = { asPath: string; category: string; pathname: string };

const getActiveItemId = ({ asPath, pathname }: GetActiveItemIdParams): string => {
	return itemIdMap[pathname === ACCOUNT.href ? asPath : pathname] || '';
};

export const useActiveItemId = (): string => {
	const router = useRouter();
	const { asPath, pathname, query } = router;
	const [category = ''] = query?.params || [];

	const activeItemId = useMemo(() => {
		return getActiveItemId({ asPath, category, pathname });
	}, [asPath, category, pathname]);

	return activeItemId;
};
